import React, { useEffect, useRef, useState } from "react";
import Product from "../ProductViews/Product";
import { useProducts } from "../../Context/ProductContext";
import { useNavigate, useParams } from "react-router-dom";
import InfoSection from "./InfoSection";
import { useNavbarCategories } from "../../Context/NavbarCategoriesContext"; // Import the category context
import Lottie from "react-lottie-player";
import animationData from "../../products/detail/./notfound.json";
import { useCategoryDetails } from "../../Context/CategoryDetailsContext";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLanguage } from "../../Context/LanguageContext";

const ProductCategory = () => {
  const { category } = useParams(); // Get category name from URL params
  const { categories } = useNavbarCategories(); // Get categories from context
  const { products, fetchProducts } = useProducts();
  const [categoryId, setCategoryId] = useState(null);
  const { loadCategoryData, categoryDetailes } = useCategoryDetails();
  const { language } = useLanguage();
  const Navigate = useNavigate();

  // Ensure category exists before processing
  useEffect(() => {
    if (category && categories.length > 0) {
      // Recursive function to search in categories and children
      const findCategoryByName = (categoryList, categoryName) => {
        for (const cat of categoryList) {
          if (cat.name.toLowerCase() === categoryName.toLowerCase()) {
            return cat; // Found category, return it
          }
          if (cat.children && cat.children.length > 0) {
            const foundInChildren = findCategoryByName(
              cat.children,
              categoryName
            );
            if (foundInChildren) return foundInChildren; // Return if found in children
          }
        }
        return null; // Not found
      };

      const foundCategory = findCategoryByName(categories, category);

      if (foundCategory) {
        setCategoryId(foundCategory.id);
      } else {
        console.warn("Category not found:", category);
      }
    }
  }, [category, categories]);

  // Fetch products when categoryId is set
  useEffect(() => {
    if (categoryId) {
      fetchProducts(categoryId, "", true, false);
      loadCategoryData(categoryId, language);
    }
  }, [categoryId, language]);

  const sliderRef = useRef(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3, // Show 3 items at a time
    slidesToScroll: 1,
    arrows: true, // Show navigation buttons
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2, // Show 2 items on tablets
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1, // Show 1 item on mobile
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div>
      <header
        className="my-5 py-5 text-center text-white position-relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1626697556739-77b40d9a6e99?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "#fff",
        }}
      >
        {/* Semi-transparent overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)", // Dark semi-transparent overlay
            zIndex: 1,
          }}
        ></div>

        {/* Text content */}
        <div
          className="py-5"
          style={{
            position: "relative",
            zIndex: 2, // Ensures text appears above the overlay
            opacity: 1, // Full text opacity
          }}
        >
          <h1>{category}</h1>
          <p className="lead">
            Explore the finest selection of {category.toLowerCase()} from our
            store.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <div className="mt-5">
        {/* Products Grid */}
        <div className="container mt-5">
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
            {products.length > 0 ? (
              products.map((product) => (
                <div className="col p-1" key={product.productId}>
                  <Product product={product} isCategoryPage={true} />
                </div>
              ))
            ) : (
              <div className="text-center container d-flex flex-column justify-content-center ">
                <div className="empty-cart d-flex justify-content-center p-0">
                  <Lottie loop animationData={animationData} play />
                </div>
                <p className="text-muted">
                  No products found in this category.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="container">
          {/* Additional Section */}
          <div className="mt-5">
            <div className="text-center mb-4">
              <h2>Why Choose Our {category}?</h2>
              <p className="text-muted">
                We offer the best quality {category}, sourced from trusted
                suppliers and delivered with care.
              </p>
            </div>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="card border-0 rounded-0 shadow h-100">
                  <div className="card-body">
                    <i className="bi bi-gem fs-1 text-primary"></i>
                    <h5 className="card-title mt-3">Premium Quality</h5>
                    <p className="card-text">
                      Only the best-grade {category} for your satisfaction.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 rounded-0 shadow h-100">
                  <div className="card-body">
                    <i className="bi bi-cart-check fs-1 text-success"></i>
                    <h5 className="card-title mt-3">Affordable Pricing</h5>
                    <p className="card-text">
                      Get premium {category} at competitive prices.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 rounded-0 shadow h-100">
                  <div className="card-body">
                    <i className="bi bi-truck fs-1 text-warning"></i>
                    <h5 className="card-title mt-3">Fast Delivery</h5>
                    <p className="card-text">
                      Quick and reliable delivery to your doorstep.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <InfoSection categoryDetailes={categoryDetailes} />
        </div>
        <hr />

        <div className="container position-relative">
          {/* Explore More Section */}
          <div className="my-5">
            <h4 className="mb-4">Explore More Categories</h4>
            <Slider ref={sliderRef} {...settings}>
              {categoryDetailes?.moreCategories?.map((category) => (
                <div key={category.id} className="px-2 py-3">
                  <div className="card border-0 rounded-0 h-100">
                    <div className="d-flex justify-content-center align-items-center">
                      <img
                        src={category?.image?.thumbnail}
                        className="img-fluid explore-more-img"
                      />
                    </div>
                    <div className="card-body">
                      <h5 className="card-title">{category.name}</h5>
                      <p
                        className="card-details"
                        dangerouslySetInnerHTML={{
                          __html:
                            category.description.length > 100
                              ? category.description.substring(0, 150) + "..."
                              : category.description,
                        }}
                      ></p>
                      <button
                        className="btn btn-green-theme btn-sm mt-3"
                        onClick={() =>
                          Navigate(`/products/category/${category.name}`)
                        }
                      >
                        View More
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>

            {/* Custom Navigation Buttons */}
            <button
              className="custom-prev-btn"
              onClick={() => sliderRef.current.slickPrev()}
            >
              ←
            </button>
            <button
              className="custom-next-btn"
              onClick={() => sliderRef.current.slickNext()}
            >
              →
            </button>
          </div>

          {/* CSS for positioning buttons */}
          <style jsx>{`
            .custom-prev-btn,
            .custom-next-btn {
              position: absolute;
              top: 10px;
              background-color: var(--theme-primary);
              color: white;
              border: none;
              border-radius: 50%;
              width: 45px;
              height: 45px;
              font-size: 30px;
              cursor: pointer;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              z-index: 1000;
              transition: background-color 0.3s, color 0.3s;
              border: 2px solid var(--theme-primary);
              display: flex;
              justify-content: center;
              align-items: center;
            }

            .custom-prev-btn {
              right: 80px;
            }

            .custom-next-btn {
              right: 20px;
            }

            .custom-prev-btn:hover,
            .custom-next-btn:hover {
              background-color: #fff;
              color: var(--theme-primary);
              border: 2px solid var(--theme-primary);
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default ProductCategory;
